import { computed, observable } from 'mobx';
import Segment, { convertSegmentResponseToArgs } from './segment2';
import type { SegmentResponseArgs } from './segment2';
import type Leg from './leg';
import type Organization from '../organization/organization3';
import type ReservingTripItem from '../trip/reserving_trip_item2';
import type { TransitPriceResponseArgs } from './transit_price';
import TransitPrice, { convertTransitPriceResponseToArgs } from './transit_price';
import type MarginType from '../organization/margin_type3';

export interface LegsummaryesTypeprice {
  type?: string;
  price?: number;
}

export type TransitResponseArgs = {
  id: string;
  segments: SegmentResponseArgs[];
  additional_price: number | null;
  price: number | null;
  is_unreserved_seat_available: boolean | null;
  legs_fromto?: string;
  legsummaries_fromto_before?: string;
  legsummaries_fromto_after?: string;
  legsummaries_fromto_jrair?: string;
  legsummaries_typeprice_before?: LegsummaryesTypeprice;
  legsummaries_typeprice_after?: LegsummaryesTypeprice;
  air: boolean;
  shinkansen: boolean;
  package: boolean;
  prices: TransitPriceResponseArgs[] | null;
  selected_text?: string;
  fee: number;
  is_rakuten_package: boolean | null;
};

export type TransitArgs = {
  id?: string;
  segments?: Segment[];
  additionalPrice?: number | null;
  price?: number | null;
  isUnreservedSeatAvailable?: boolean | null;
  parentItem?: ReservingTripItem;
  legsFromto?: string;
  legsummariesFromtoBefore?: string;
  legsummariesFromtoAfter?: string;
  legsummariesFromtoJrair?: string;
  legsummariesTypepriceBefore?: LegsummaryesTypeprice;
  legsummariesTypepriceAfter?: LegsummaryesTypeprice;
  air?: boolean;
  shinkansen?: boolean;
  package?: boolean;
  prices?: TransitPrice[];
  selectedText?: string;
  fee?: number;
  isRakutenPackage?: boolean | null;
};

export const convertTransitResponseToArgs = (response: TransitResponseArgs) => {
  const args: TransitArgs = {
    id: response.id,
    segments: response.segments.map(raw => new Segment(convertSegmentResponseToArgs(raw))),
    additionalPrice: response.additional_price,
    price: response.price,
    isUnreservedSeatAvailable: response.is_unreserved_seat_available,
    legsFromto: response.legs_fromto,
    legsummariesFromtoBefore: response.legsummaries_fromto_before,
    legsummariesFromtoAfter: response.legsummaries_fromto_after,
    legsummariesFromtoJrair: response.legsummaries_fromto_jrair,
    legsummariesTypepriceBefore: response.legsummaries_typeprice_before,
    legsummariesTypepriceAfter: response.legsummaries_typeprice_after,
    air: response.air,
    shinkansen: response.shinkansen,
    package: response.package,
    prices: response.prices?.map(raw => new TransitPrice(convertTransitPriceResponseToArgs(raw))) ?? [],
    selectedText: response.selected_text,
    fee: response.fee,
    isRakutenPackage: response.is_rakuten_package
  };
  return args;
};

export default class Transit {
  @observable id?: string;
  @observable segments: Segment[];
  @observable additionalPrice: number | null | undefined;
  @observable price: number | null;
  @observable isUnreservedSeatAvailable: boolean | null;
  @observable parentItem?: ReservingTripItem;
  @observable legsFromto?: string;
  @observable legsummariesFromtoBefore?: string;
  @observable legsummariesFromtoAfter?: string;
  @observable legsummariesFromtoJrair?: string;
  @observable legsummariesTypepriceBefore?: LegsummaryesTypeprice;
  @observable legsummariesTypepriceAfter?: LegsummaryesTypeprice;
  @observable air?: boolean;
  @observable shinkansen?: boolean;
  @observable package?: boolean;
  @observable prices?: TransitPrice[];
  @observable selectedText?: string;
  @observable fee: number;
  @observable isRakutenPackage: boolean | null;

  constructor(args: TransitArgs = {}) {
    this.id = args.id;
    this.segments = args.segments || [];
    this.additionalPrice = args.additionalPrice;
    this.price = args.price || null;
    this.isUnreservedSeatAvailable = args.isUnreservedSeatAvailable || null;
    this.parentItem = args.parentItem;
    this.legsFromto = args.legsFromto;
    this.legsummariesFromtoBefore = args.legsummariesFromtoBefore;
    this.legsummariesFromtoAfter = args.legsummariesFromtoAfter;
    this.legsummariesFromtoJrair = args.legsummariesFromtoJrair;
    this.legsummariesTypepriceBefore = args.legsummariesTypepriceBefore;
    this.legsummariesTypepriceAfter = args.legsummariesTypepriceAfter;
    this.air = args.air ?? false;
    this.shinkansen = args.shinkansen ?? false;
    this.package = args.package ?? false;
    this.prices = args.prices ?? [];
    this.selectedText = args.selectedText;
    this.fee = args.fee ?? 0;
    this.isRakutenPackage = args.isRakutenPackage ?? null;
  }

  @computed get organization(): Organization | undefined {
    return this.parentItem?.organization;
  }

  @computed get airExists() {
    return this.segments.some(s => s.isAir);
  }

  @computed get shinkansenExists() {
    return this.segments.some(s => s.type === 'shin');
  }

  @computed get expressExists() {
    return this.segments.some(s => s.type === 'express');
  }

  @computed get totalPrice() {
    if (this.showFee) {
      return this.totalTicketPrice + this.marginAmount;
    }
    return this.totalTicketPrice;
  }

  @computed get startDate() {
    return this.segments[0]?.legs[0]?.from?.timeFull?.split('T')[0];
  }

  @computed get lastSegment(): Segment | undefined {
    return this.segments[this.segments.length - 1];
  }

  @computed get lastLeg(): Leg | undefined {
    if (!this.lastSegment) return undefined;
    return this.lastSegment.legs[this.lastSegment.legs.length - 1];
  }

  @computed get endDate() {
    if (!this.lastLeg) return undefined;
    return this.lastLeg.to?.timeFull?.split('T')[0];
  }

  @computed get reserveConfirmElementLabel(): string {
    if (this.air) {
      return '飛行機';
    }
    if (this.shinkansen) {
      return '新幹線';
    }
    return '在来線';
  }

  @computed get reserveConfirmElementPrice(): number {
    return this.totalPrice;
  }

  @computed get ticketPrice(): number {
    if (this.airExists) {
      if (this.parentItem?.domesticAirPriceIndex == null) {
        return 0;
      }
      // select画面の右側の選択肢を選択した瞬間、this.pricesが選択前の要素を持っているのに、
      // this.domesticAirPriceIndexのみ選択後の要素を指す。そのときはエラーではなく、0を返す
      return this.prices == null ||
        this.prices.length < this.parentItem.domesticAirPriceIndex ||
        this.prices[this.parentItem.domesticAirPriceIndex] === undefined ||
        this.prices[this.parentItem.domesticAirPriceIndex] === null
        ? 0
        : this.prices[this.parentItem.domesticAirPriceIndex].price;
    }
    if (this.shinkansenExists || this.expressExists) {
      return this.price ?? 0;
    }
    return 0;
  }

  @computed get ticketPriceText(): string {
    return `${utils.formatPrice(this.ticketPrice)} × ${this.parentItem?.peopleNum ?? 0}名`;
  }

  @computed get showFee(): boolean {
    return this.parentItem?.showFee ?? true;
  }

  @computed get marginType(): MarginType | undefined {
    return this.parentItem?.marginType;
  }

  @computed get peopleNum(): number {
    return this.parentItem?.peopleNum ?? 1;
  }

  @computed get totalTicketPrice(): number {
    return this.ticketPrice * this.peopleNum;
  }

  @computed get marginAmount(): number {
    if (this.marginType) {
      return this.marginType.calcMarginAmount(this.totalTicketPrice, this.peopleNum);
    }
    return this.fee * this.peopleNum;
  }

  @computed get feeText(): string {
    if (this.marginType) {
      return this.marginType.describe(this.totalTicketPrice, this.peopleNum);
    }
    // feeはTransit::SHINKANSEN_FEE or Transit::DOMESTIC_AIR_FEE
    return `${utils.formatPrice(this.fee)} × ${this.peopleNum}名`;
  }

  @computed get priceDetailArray(): { title: string; price: string }[] {
    const arr = [];
    arr.push({ title: 'チケット代金:', price: this.ticketPriceText });
    if (this.parentItem?.showFee && this.marginAmount > 0) {
      arr.push({ title: '発券手数料:', price: this.feeText });
    }
    return arr;
  }

  @computed get totalBufferAmount(): number {
    if (!this.organization || !this.airExists || !this.parentItem) {
      return 0;
    }
    return this.organization.getDomesticFlightBufferAmount() * this.parentItem.peopleNum;
  }

  @computed get bufferAmountText(): string {
    return `${utils.digits(this.organization?.getDomesticFlightBufferAmount() ?? 0)}円 × ${
      this.parentItem?.peopleNum ?? 0
    }名`;
  }

  @computed get priceDetailText(): string {
    let text = `└ チケット代金: ${this.ticketPriceText}`;
    if (this.showFee && this.marginAmount > 0) text += `\n└ 発券手数料: ${this.feeText}`;
    return text;
  }

  @computed get priceText(): string {
    return `：${utils.formatPrice(this.totalPrice)}\n${this.priceDetailText}`;
  }

  @computed get transitPriceText(): string {
    return `経路${this.priceText}`;
  }

  @computed get reserveConfirmElementLabelByTypeBefore(): string {
    switch (this.legsummariesTypepriceBefore?.type) {
      case 'train':
        return '在来線';
      case 'bus':
        return 'バス';
      default:
        return 'その他';
    }
  }

  @computed get reserveConfirmElementLabelByTypeAfter(): string {
    switch (this.legsummariesTypepriceAfter?.type) {
      case 'train':
        return '在来線';
      case 'bus':
        return 'バス';
      default:
        return 'その他';
    }
  }
}
